import { useEffect, useState } from 'react'
import Layout from '@/components/layout/layout'
import { Box, Flex, Grid, Heading } from '@chakra-ui/react'
import ButtonMore from '@/components/layout/action/button-more'
import ModalActionsDashboard from '@/components/modal/modal-actions-dashboard'
import UserCard from '@/components/cards/user-card'
import JwtPayload from '@/entities/api/jwt'
import { AppDispatch, RootState } from '@/store/store'
import { useDispatch, useSelector } from 'react-redux'
import Toast from '@/components/elements/toast'
import { fetchUsers } from '@/services/api/users/users'
import { sessionRefrestUpdate } from '@/store/features/state'
import { setSelectUserFn, setUsersFn } from '@/store/features/users'
import SpinnerLoading from '@/components/elements/spinner'
import Search from '@/components/elements/search'
import Filter from '@/components/elements/filter'
import UsersEntity, { UsersPositions } from '@/entities/api/users/users'
import { filtersMapUserPositions } from '@/utils/maps/positions-map'
import NotData from '@/components/elements/not-data'

const Users = ({ data }: { data: JwtPayload }) => {
	const [modalAddOpen, setmodalAddOpen] = useState<Boolean>(false)
	const [modalEditOpen, setmodalEditOpen] = useState<Boolean>(false)
	const usersRefrest = useSelector(
		(state: RootState) => state.state.usersRefrest
	)
	const token = useSelector((state: RootState) => state.auth.token)
	const users = useSelector((state: RootState) => state.users.users)
	const [isLoading, setIsloading] = useState<boolean>(false)
	const dispatch = useDispatch<AppDispatch>()
	const [search, setSearch] = useState<string>()
	const [filter, setFilter] = useState<UsersPositions>()
	const [usersFilter, setUsersFilter] = useState<UsersEntity[] | []>()

	useEffect(() => {
		const getUsers = async () => {
			try {
				setIsloading(true)

				if (users.length === 0) {
					const response = await fetchUsers(token ?? '')
					if (response?.status === 401) {

						dispatch(sessionRefrestUpdate())
						return
					}
					dispatch(setUsersFn(response?.data ?? []))
				}

				setIsloading(false)
			} catch (error) {
				Toast({ type: 'error', message: 'No se pudo obtener los usuarios' })
				setIsloading(false)
			}
		}

		getUsers()
	}, [usersRefrest])

	const openEditModal = async (user: UsersEntity) => {
		dispatch(setSelectUserFn(user))
		setmodalEditOpen(true)
	}

	useEffect(() => {
		if (search && search.trim().length > 0) {
			const term = search.toLowerCase()
			setUsersFilter(
				users?.filter(
					(cat) =>
						cat.name.toLowerCase().includes(term) ||
						cat.position.toLowerCase().includes(term) ||
						cat.email.toLowerCase().includes(term) ||
						cat.phone.toString().includes(term) ||
						cat.lastname.toLowerCase().includes(term)
				) ?? []
			)
			return
		}

		if (filter && filter !== UsersPositions.administration) {
			setUsersFilter(users?.filter((cat) => cat.position === filter) ?? [])
			return
		}

		setUsersFilter(users ?? [])
	}, [search, filter, users])

	const filterOpen = (value: UsersPositions) => {
		setFilter(value)
		setSearch('')
	}

	return (
		<Layout data={data}>
			{isLoading ? (
				<SpinnerLoading />
			) : (
				<>
					<Flex
						w='full'
						p='4'
						alignItems='center'
						flexDirection={{ base: 'column', lg: 'row' }}
						justifyContent={{ base: 'start', lg: 'space-between' }}
					>
						<Heading
							as='h2'
							color='black'
							fontSize={{ base: '2xl', sm: '3xl' }}
							textAlign={{ base: 'center', sm: 'start' }}
							whiteSpace='normal'
							wordBreak='break-word'
							fontWeight='800'
						>
							Gestion de usuarios
						</Heading>

						<Box
							w={{ base: 'full', xl: 'auto' }}
							flexDirection={{ base: 'column', sm: 'row' }}
							display='flex'
							alignItems='center'
							justifyContent={{ base: 'start', sm: 'center' }}
							p='4'
						>
							<ButtonMore click={() => setmodalAddOpen(true)} />
							<Box ml={{ base: '0', lg: '4' }}>
								<Search setSearch={setSearch} />
							</Box>
							<Box ml={{ base: '0', lg: '4' }}>
								<Filter
									label='Tipo :'
									filters={filtersMapUserPositions}
									setFilter={filterOpen}
								/>
							</Box>
						</Box>
					</Flex>
					<Box w='full' p='6'>
						{usersFilter && usersFilter.length > 0 ? (
							<Grid
								w='full'
								templateColumns={{
									base: '1fr',
									sm: 'repeat(2, 1fr)',
									lg: 'repeat(4, 1fr)',
									xl: 'repeat(6, 1fr)',
								}}
								position='relative'
								gap='4'
							>
								{usersFilter.map((user, index) => {
									if (user._id !== data.sub) {
										return (
											<UserCard
												key={user._id ?? index}
												token={token ?? ''}
												user={user}
												click={() => openEditModal(user)}
											/>
										)
									} else if (users.length === 1) {
										return (
											<Box key='no-data' w='full' position='absolute'>
												<NotData />
											</Box>
										)
									} else {
										return null
									}
								})}
							</Grid>
						) : (
							<NotData />
						)}
					</Box>
				</>
			)}
			{modalAddOpen && (
				<ModalActionsDashboard
					open={modalAddOpen}
					action='add-user'
					close={() => setmodalAddOpen(false)}
				/>
			)}
			{modalEditOpen && (
				<ModalActionsDashboard
					open={modalEditOpen}
					action='edit-user'
					close={() => setmodalEditOpen(false)}
				/>
			)}
		</Layout>
	)
}

export default Users
